import { Box, ToggleButtonGroup } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'

export const Container = styled(Box)`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;

  ::-webkit-scrollbar {
    width: 4px;
    background: transparent;
    max-height: 50%;
    display: block;
  }

  ::-webkit-scrollbar-track {
    box-shadow: none;
    background: ${theme.colors.legacyBorderLow2};
    margin-top: 222px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.legacyTextPrimary2};
    border-radius: ${theme.attributes.borderRadius.quaternary};
    html[data-theme='light'] & {
      background-color: ${theme.colors.borderMedium};
    }
  }
`

export const EmptySSMGridContainer = styled(Box)<{ opacity: number }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ opacity }) => `${opacity === 0 ? '0px !important' : `${theme.spacing.small}px !important`}`};
  transition: height 0.2s ease-in-out;
  height: ${({ opacity }) => `${opacity === 1 ? '40px' : '0'}`};
  overflow: hidden;

  .empty-message-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: ${theme.spacing.xSmall}px ${theme.spacing.small}px;
    background: ${theme.colors.chipNewBg};
    border-radius: ${theme.attributes.borderRadius.small};
    height: 40px;

    .empty-message-inner {
      display: flex;
      align-items: center;
      gap: ${theme.spacing.sm}px;
      flex-grow: 1;
      width: 100%;

      .empty-message-text {
        color: ${theme.colors.foreground};
        text-align: center;
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.xLarge};
        font-style: normal;
        font-weight: ${theme.typography.fontWeight.normal};
        line-height: normal;
      }
    }
  }
`

export const Header = styled(Box)`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: ${theme.spacing.small}px ${theme.spacing.medium}px 0;
  height: 130px;
  background: ${theme.colors.surfaceContainerBackground};

  .header-top {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    flex-grow: 1;

    .header-title {
      color: ${theme.colors.menuItemText};
      line-height: 48px;
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.xxxLarge};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.bold};
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 12px;

      .MuiIconButton-root {
        border-radius: 12px;
        border: 1px solid ${theme.colors.dropdownBorder};
        width: 48px;
        height: 48px;
      }
    }
  }

  .header-right-corner {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spacing.small}px;
    min-width: 202px;

    .total-products-text {
      color: ${theme.colors.foreground};
      text-align: center;
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.xLarge};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.normal};
      line-height: normal;
      white-space: nowrap;
    }

    .MuiDivider-root {
      background: ${theme.colors.legacyBorderLow2};
    }

    .MuiIconButton-root {
      width: 40px;
      height: 40px;
      color: ${theme.colors.cta};
    }
  }
`

export const ViewToggle = styled(ToggleButtonGroup)`
  height: 40px;

  .MuiToggleButton-root {
    height: 40px;
    padding: 0 ${theme.spacing.medium}px;
    border: 1px solid ${theme.colors.controlBorder};
    color: ${theme.colors.cta};
    font-family: ${theme.typography.fontFamily.primary};
    text-align: center;
    font-size: ${theme.typography.fontSize.xLarge};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.thin};
    line-height: normal;
    text-transform: none;
    width: 164px;
    white-space: nowrap;

    &:first-of-type {
      border-radius: ${theme.attributes.borderRadius.large} 0 0 ${theme.attributes.borderRadius.large};

      &.Mui-selected {
        color: ${theme.colors.foreground};
        background: ${theme.baseColors.primary[20]};

        &:hover {
          background: ${theme.baseColors.primary[20]};
        }
      }
    }

    &:last-of-type {
      border-radius: 0 ${theme.attributes.borderRadius.large} ${theme.attributes.borderRadius.large} 0;

      &:hover {
        border-left: 1px solid ${theme.colors.controlBorder};
      }

      &.Mui-selected {
        border-left: 1px solid ${theme.colors.controlBorder};
        color: ${theme.colors.foreground};
        background: ${theme.baseColors.primary[20]};

        &:hover {
          background: ${theme.baseColors.primary[20]};
          border-left: 1px solid ${theme.colors.controlBorder};
        }
      }
    }

    &.Mui-selected {
      color: ${theme.colors.foreground};
      background: ${theme.baseColors.primary[20]};
    }
  }
`

export const ContentContainer = styled(Box)`
  width: 100%;
  padding: ${theme.spacing.small}px ${theme.spacing.medium}px ${theme.spacing.medium}px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: ${theme.colors.modalSurface};

  html[data-theme='light'] & {
    background: ${theme.colors.pageBackground};
  }

  ::-webkit-scrollbar {
    width: 4px;
    background: transparent;
  }

  ::-webkit-scrollbar-track {
    box-shadow: none;
    background: ${theme.colors.legacyBorderLow2};
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.legacyTextPrimary2};
    border-radius: ${theme.attributes.borderRadius.quaternary};
    html[data-theme='light'] & {
      background-color: ${theme.colors.borderMedium};
    }
  }
`

export const ControlsContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${theme.colors.surfaceContainerBackground};

  html[data-theme='light'] & {
    background: ${theme.colors.pageBackground};
  }

  .total-products-wrapper {
    display: flex;
    align-items: center;
    gap: 6px;
    padding-right: 24px;

    .total-products {
      color: ${theme.colors.cardDateText};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 22px;
    }

    .total-products-count {
      color: ${theme.colors.foreground};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 24px;

      span {
        color: ${theme.colors.textSecondary};
        font-family: ${theme.typography.fontFamily.primary};
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 22px;
      }
    }
  }
`

export const ControlsWrapper = styled(Box)`
  padding-top: 24px;
  margin-left: 0;
  padding-left: 8px;
  display: flex;
  align-items: center;
  gap: 16px;
`

export const SSMGrid = styled(Box)`
  display: grid;
  grid-template-columns: 124px 1fr 1fr 1fr 1fr 1fr;
  grid-template-rows: 45px 1fr 1fr 1fr 1fr 1fr 1fr;
  flex: 1;
  min-height: 0;
`

export const GridHeader = styled(Box)`
  display: flex;
  padding: ${theme.spacing.xSmall}px ${theme.spacing.small}px;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${theme.colors.strokePrimary};

  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 24px;
    letter-spacing: 0.1px;
    color: ${theme.colors.menuItemText};
  }

  .header-value {
    display: flex;
    align-items: baseline;
    gap: 4px;
    color: ${theme.colors.calendarDay};
    white-space: nowrap;

    .header-value-count {
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.xLarge};
      font-weight: ${theme.typography.fontWeight.extraThick};
      line-height: 24px;
      letter-spacing: 0.5px;
      overflow: hidden;
      text-overflow: ellipsis;

      html[data-theme='light'] & {
        color: ${theme.colors.foreground};
      }
    }

    .header-value-label {
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.large};
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 22px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .header-value-label-short {
      display: none;
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.medium};
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 22px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  @media screen and (max-width: 1200px) {
    .name {
      font-size: ${theme.typography.fontSize.large};
    }

    .header-value .header-value-count {
      font-size: ${theme.typography.fontSize.large};
    }

    .header-value .header-value-label {
      display: none;
    }

    .header-value .header-value-label-short {
      display: inline;
    }
  }
`

export const CornerCell = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${theme.colors.strokePrimary};
  border-radius: ${theme.attributes.borderRadius.tertiary} 0 0 0;
  padding: 7px ${theme.spacing.small}px;
  overflow: clip;
  color: ${theme.colors.foreground};
`

export const BasicCell = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${theme.spacing.small}px;
  border: 1px solid ${theme.colors.strokePrimary};

  .nist-top {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.sm}px;
  }

  .nist-pillar-text {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 24px;
    letter-spacing: 0.1px;
  }

  .nist-value-text {
    display: flex;
    align-items: flex-end;
    gap: 4px;
    color: ${theme.colors.calendarDay};
    white-space: nowrap;

    .nist-value-count {
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.xLarge};
      font-weight: ${theme.typography.fontWeight.extraThick};
      line-height: 22px;
      letter-spacing: 0.5px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .nist-value-label {
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.large};
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 20px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  @media screen and (max-width: 1200px) {
    .nist-pillar-text {
      font-size: ${theme.typography.fontSize.large};
    }

    .nist-value-text .nist-value-count {
      font-size: ${theme.typography.fontSize.large};
    }

    .nist-value-text .nist-value-label {
      font-size: ${theme.typography.fontSize.medium};
    }
  }
`

export const ComingSoonContainer = styled(Box)`
  position: relative;

  .coming-soon-wrapper {
    position: absolute;
    top: -19px;
    left: 200px;

    .chip-title {
      white-space: nowrap;
      text-transform: none;
    }
  }

  .label-text {
    color: ${theme.colors.calendarDay} !important;
    font-family: Quicksand, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 22px;
  }
`
